import React, { useEffect, useState, useCallback } from 'react';
import { SettingCard } from '../../components/SettingCards/SettingCard/SettingCard';
import { SettingsNativeBridgeFactory } from '../../bridge/SettingsNativeBridgeFactory';
import { SettingsLogger, logConstants, AotCategory } from '../../utils/SettingsLogger';
import { DeviceInfoDescriptionHeader } from './DeviceInfoDescriptionHeader';
import { ToggleSwitch } from '../../components/ToggleSwitch/ToggleSwitch';
import { Dropdown, DropdownOption } from '../../components/Dropdown/Dropdown';
import { DataTable, DataTableColumn } from '../../components/DataTable/DataTable';
import { InfoAlertIcon } from '../../Icons/AlertIcons/AlertIcons';
import './DevicesInfo.css';

export interface DeviceInfo {
	deviceId: string;
	deviceName: string;
	parentName: string;
	vendorId: string;
	productId: string;
	currentConnections: string[];
	redirectToSession: boolean;
	virtualChannel: string;
	IsVirtualChannelChangeAllowed?: boolean;
	IsUpdateAllowed?: boolean;
}

interface DevicesInfoProps {
	title?: string;
	description?: string;
	learnMoreUrl?: string;
	learnMoreText?: string;
}

export const DevicesInfo: React.FC<DevicesInfoProps> = ({
	description = '',
	learnMoreUrl,
	learnMoreText,
}) => {
	const [devices, setDevices] = useState<DeviceInfo[]>([]);
	const [isLoading, setIsLoading] = useState<boolean>(true);
	const [isRefreshing, setIsRefreshing] = useState<boolean>(false);

	const virtualChannelOptions: DropdownOption[] = [
		{ value: 'Optimized', label: 'Optimized' },
		{ value: 'Generic', label: 'Generic' },
	];

	const loadDevices = useCallback(async () => {
		try {
			const bridge = SettingsNativeBridgeFactory.getBridge();
			const response = await bridge.getSetting({
				settingKey: 'devices-info',
			});

			if (response.status === 0 && response.data?.settingValue) {
				const devicesData =
					typeof response.data.settingValue === 'string'
						? JSON.parse(response.data.settingValue)
						: response.data.settingValue;

				setDevices(devicesData || []);
			} else {
				setDevices([]);
			}
		} catch (err) {
			SettingsLogger.getInstance().write(
				logConstants.ERROR,
				`Failed to load devices info: ${err}`,
				AotCategory.Error,
				`Failed to load devices info: ${err}`
			);
			setDevices([]);
		} finally {
			setIsLoading(false);
			setIsRefreshing(false);
		}
	}, []);

	useEffect(() => {
		loadDevices();
	}, [loadDevices]);

	const handleRefresh = async () => {
		setIsRefreshing(true);
		await loadDevices();
	};

	const handleToggleRedirect = async (deviceId: string, currentValue: boolean) => {
		try {
			// Update local state optimistically
			setDevices((prevDevices) =>
				prevDevices.map((device) =>
					device.deviceId === deviceId
						? { ...device, redirectToSession: !currentValue }
						: device
				)
			);

			// Send update to native
			const bridge = SettingsNativeBridgeFactory.getBridge();
			await bridge.setSetting({
				settingKey: 'device-redirect',
				settingValue: JSON.stringify({
					deviceId,
					redirectToSession: !currentValue,
				}),
			});
		} catch (err) {
			SettingsLogger.getInstance().write(
				logConstants.ERROR,
				`Failed to toggle device redirect: ${err}`,
				AotCategory.Error,
				`Failed to toggle device redirect: ${err}`
			);
			// Revert on error
			await loadDevices();
		}
	};

	const handleVirtualChannelChange = async (deviceId: string, newChannel: string) => {
		try {
			// Update local state optimistically
			setDevices((prevDevices) =>
				prevDevices.map((device) =>
					device.deviceId === deviceId
						? { ...device, virtualChannel: newChannel }
						: device
				)
			);

			// Send update to native
			const bridge = SettingsNativeBridgeFactory.getBridge();
			await bridge.setSetting({
				settingKey: 'device-virtual-channel',
				settingValue: JSON.stringify({
					deviceId,
					virtualChannel: newChannel,
				}),
			});
		} catch (err) {
			SettingsLogger.getInstance().write(
				logConstants.ERROR,
				`Failed to update virtual channel: ${err}`,
				AotCategory.Error,
				`Failed to update virtual channel: ${err}`
			);
			// Revert on error
			await loadDevices();
		}
	};

	const columns: DataTableColumn<DeviceInfo>[] = [
		{
			key: 'device',
			header: 'Device',
			width: '28%',
			cellType: 'custom',
			render: (device) => (
				<div className="device-info-cell">
					<div className="device-name-row">
						<span className="device-name">{device.deviceName}</span>
						<span
							className="device-info-icon"
							data-tooltip={`PID: ${device.productId} | VID: ${device.vendorId}`}
							aria-label={`PID: ${device.productId}, VID: ${device.vendorId}`}
						>
							<InfoAlertIcon width={14} height={14} fill="#FFFFFF" color="#767676" />
						</span>
					</div>
					<div className="device-parent-name">{device.parentName}</div>
				</div>
			),
		},
		{
			key: 'currentConnections',
			header: 'Current connection',
			width: '24%',
			cellType: 'custom',
			render: (device) => (
				<div className="device-connection-cell">
					{device.currentConnections.map((connection, index) => (
						<div key={index} className="connection-line">
							{connection}
						</div>
					))}
				</div>
			),
		},
		{
			key: 'redirectToSession',
			header: 'Redirect to session',
			width: '24%',
			cellType: 'toggle',
			render: (device) => (
				<ToggleSwitch
					checked={device.redirectToSession}
					onChange={() => handleToggleRedirect(device.deviceId, device.redirectToSession)}
					ariaLabel={`Toggle redirect to session for ${device.deviceName}`}
					disabled={device.IsUpdateAllowed === false}
				/>
			),
		},
		{
			key: 'virtualChannel',
			header: 'Virtual channel',
			width: '24%',
			cellType: 'custom',
			render: (device) =>
				device.IsVirtualChannelChangeAllowed === false ||
				device.IsUpdateAllowed === false ? (
					<span className="policy-restricted-text">Policy restricted</span>
				) : (
					<Dropdown
						options={virtualChannelOptions}
						value={device.virtualChannel}
						onChange={(value) => handleVirtualChannelChange(device.deviceId, value)}
						showLabel={false}
						className="device-virtual-channel-dropdown"
					/>
				),
		},
	];

	const renderEmptyState = () => (
		<div className="devices-info-empty-state">
			<svg
				xmlns="http://www.w3.org/2000/svg"
				width="40"
				height="40"
				viewBox="0 0 40 40"
				fill="none"
				className="devices-info-empty-icon"
			>
				<path
					fillRule="evenodd"
					clipRule="evenodd"
					d="M28.75 33.75H36.25V38.75H28.75V33.75Z"
					stroke="#767676"
					strokeWidth="2.08333"
					strokeLinecap="round"
					strokeLinejoin="round"
				/>
				<path
					fillRule="evenodd"
					clipRule="evenodd"
					d="M38.75 33.7503H26.25V23.877C26.25 22.4962 27.3693 21.377 28.75 21.377H36.25C37.6307 21.377 38.75 22.4962 38.75 23.877V33.7503Z"
					stroke="#767676"
					strokeWidth="2.08333"
					strokeLinecap="round"
					strokeLinejoin="round"
				/>
				<path
					fillRule="evenodd"
					clipRule="evenodd"
					d="M3.75 1.25H11.25V6.25H3.75V1.25Z"
					stroke="#767676"
					strokeWidth="2.08333"
					strokeLinecap="round"
					strokeLinejoin="round"
				/>
				<path
					fillRule="evenodd"
					clipRule="evenodd"
					d="M13.75 6.25H1.25V16.3767C1.25 17.7574 2.36929 18.8767 3.75 18.8767H11.25C12.6307 18.8767 13.75 17.7574 13.75 16.3767V6.25Z"
					stroke="#767676"
					strokeWidth="2.08333"
					strokeLinecap="round"
					strokeLinejoin="round"
				/>
				<path
					d="M32.5 21.3767V7.5C32.5 4.04822 29.7018 1.25 26.25 1.25C22.7982 1.25 20 4.04822 20 7.5V32.5C20 35.9518 17.2018 38.75 13.75 38.75C10.2982 38.75 7.5 35.9518 7.5 32.5V18.8767"
					stroke="#767676"
					strokeWidth="2.08333"
					strokeLinecap="round"
					strokeLinejoin="round"
				/>
			</svg>
			<p className="devices-info-empty-text">No connected devices found.</p>
		</div>
	);

	const renderContent = () => {
		if (isLoading) {
			return <div className="devices-info-loading">Loading devices...</div>;
		}

		if (devices.length === 0) {
			return renderEmptyState();
		}

		return (
			<DataTable<DeviceInfo>
				columns={columns}
				data={devices}
				keyExtractor={(device) => device.deviceId}
				className="devices-info-table"
				ariaLabel="Connected devices"
				resizable
			/>
		);
	};

	return (
		<SettingCard
			title={''}
			description={''}
			settingId="devices-info"
			content={
				<>
					<DeviceInfoDescriptionHeader
						description={description}
						learnMoreUrl={learnMoreUrl}
						learnMoreText={learnMoreText}
						onRefresh={handleRefresh}
						isRefreshing={isRefreshing}
					/>
					<div className={`devices-info-content ${isRefreshing ? 'refreshing' : ''}`}>
						{renderContent()}
					</div>
				</>
			}
		/>
	);
};
